import { isEmpty, isString, toNumber } from 'lodash';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { Loader, TravelLodgings, TravelParticipants, useTravelPurchaseContext } from 'components';
import { formatDate } from 'helpers';
import { useFetch, useSafeState, useUser } from 'hooks';
import { setTravelPurchase } from 'store';

const TravelConfigurationStep = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { next } = useTravelPurchaseContext();
  const { watch, setValue } = useFormContext();
  const [searchParams] = useSearchParams();
  const { currentUser } = useUser();
  const {
    call: lodgingsCall,
    data: lodgings,
    setData: setLodgings,
    loading: lodgingsLoading
  } = useFetch({ withSetState: false });
  const [lodgingError, setLodgingError] = useSafeState('');
  const { updateTravelPurchaseLoading } = useTravelPurchaseContext();

  const storedDepartureCity = useSelector((state) => state.travelPurchase.departureCity);
  const storedDepartureDate = useSelector((state) => state.travelPurchase.departureDate);
  const storedTravelDuration = useSelector((state) => state.travelPurchase.duration);
  const storedPassengerCategories = useSelector((state) => state.travelPurchase.passengersCat);
  const storedLodging = useSelector((state) => state.travelPurchase.lodging);

  const participants = watch('configuration.participants');
  const lodging = watch('configuration.lodging');
  const participantsFilled = participants?.every(
    (participant) => participant.firstName && participant.name && participant.birthDate
  );

  const isValid = participantsFilled && !isEmpty(lodging);

  // -----------------------------------------------------------------------------------------------------
  // Lifecycle hooks
  // -----------------------------------------------------------------------------------------------------
  useEffect(() => {
    getLodgings();
  }, []);

  // -----------------------------------------------------------------------------------------------------
  // Methods
  // -----------------------------------------------------------------------------------------------------
  const getLodgings = async () => {
    const travelId = searchParams.get('travelId');
    const typeItem = searchParams.get('typeItem');
    let typeParticipants = storedPassengerCategories.filter((participant) => participant.qty > 0);

    if (!isEmpty(typeParticipants)) {
      // API needs a specific format for the typeParticipants array. Each object must be stringified.
      typeParticipants = typeParticipants.map((participant) => JSON.stringify(participant));
    }

    // Reset error message
    setLodgingError('');

    const { data } =
      (await lodgingsCall(
        {
          service: 'castelis',
          path: `/product/travels/${currentUser.serialNumber}/departure/${travelId}/${typeItem}`,
          method: 'post',
          query: {
            departureCityId: toNumber(storedDepartureCity?.value),
            duration: toNumber(storedTravelDuration?.value),
            departureDate: formatDate(storedDepartureDate, 'dd/MM/yyyy'),
            typeParticipants
          }
        },
        { hideToast: true }
      )) || {};

    const accomodations = data?.body?.accomodations;

    if (!isEmpty(accomodations)) {
      /* Format lodgings from API response to add properties for the RadioGroup */
      const options = accomodations.map((accomodation) => ({
        name: 'configuration.lodging',
        value: accomodation.compositionId,
        label: accomodation.label,
        uuid: accomodation.compositionId,
        price: accomodation.price,
        sojournId: accomodation.sojournId
      }));
      setLodgings(options);

      if (storedLodging?.compositionId) {
        // Init form with stored values to prefill radio button
        setValue('configuration.lodging', storedLodging.compositionId);
      } else if (options.length === 1) {
        // If there is no stored value and if there is only one option, prefills the radio button
        setValue('configuration.lodging', options[0].value);
      }
    } else {
      if (data?.status === 400 && !isEmpty(data?.body) && isString(data?.body)) {
        setLodgingError(data.body);
      }
    }
  };

  const onNextClick = () => {
    if (!isValid) return;
    const lodgingOption = lodgings?.find((option) => option.value === lodging);

    // Save participants and lodging in the store
    dispatch(setTravelPurchase({ key: 'participants', value: participants }));
    if (lodgingOption)
      dispatch(
        setTravelPurchase({
          key: 'lodging',
          value: {
            label: lodgingOption.label,
            compositionId: lodgingOption.value,
            price: lodgingOption.price,
            sojournId: lodgingOption.sojournId
          }
        })
      );

    // Navigate to next step
    next();
  };

  return (
    <>
      <TravelParticipants />
      <TravelLodgings lodgings={lodgings} loading={lodgingsLoading} lodgingError={lodgingError} />

      <button
        type="button"
        className="btn purchase-btn"
        disabled={!isValid || lodgingsLoading || updateTravelPurchaseLoading}
        onClick={onNextClick}
      >
        {updateTravelPurchaseLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
        {t('buttons.continue')}
      </button>
    </>
  );
};

export default TravelConfigurationStep;
